import type { SiteTopology } from "../types";

/**
 * BESS Site 1 — synthetic demo site (no real-world counterpart).
 *
 * A larger industrial profile: 4× BESS cabinets on a shared bus + a grid
 * coupling meter, no on-site PV. Used by the in-browser simulator and as the
 * fallback "demo" topology when the SPA is embedded in ThingsBoard but no real
 * BESS-site asset is bound to the widget's entity alias.
 */
export const bessSite1: SiteTopology = {
  id: "bess-site-1",
  name: "BESS Site 1",
  shortLabel: "Site 1",
  timezone: "Europe/Rome",
  aggregation: {
    grid_W: "smartmeter/aggregation/grid_W",
    grid_VAr: "smartmeter/aggregation/grid_VAr",
    battery_power_W: "battery/aggregation/power",
    battery_soc_avg: "battery/aggregation/SOC",
    pv_power_W: undefined,
  },
  bess: ["11", "12", "13", "14"].map((octet, i) => ({
    id: `bess-${i + 1}`,
    name: `BESS-${i + 1}`,
    family: "zhiguang",
    protocol: "iec-104",
    host: `192.168.3.${octet}`,
    port: 2404,
    capacity_kWh: 372.5,
    max_charge_kW: 185,
    max_discharge_kW: 185,
    invert: {
      active_power_measurement: false,
      active_power_setpoint: false,
      reactive_power_measurement: false,
      reactive_power_setpoint: false,
    },
    topics: {
      soc: `battery/measurement/${i + 1}/SOC`,
      ac_active_power: `battery/measurement/${i + 1}/ac_active_power`,
      ac_reactive_power: `battery/measurement/${i + 1}/ac_reactive_power`,
      total_voltage: `battery/measurement/${i + 1}/Total_Voltage`,
      ambient_temperature: `battery/measurement/${i + 1}/ambient_temperature`,
      accumulated_charge: `battery/measurement/${i + 1}/accumulated/positive`,
      accumulated_discharge: `battery/measurement/${i + 1}/accumulated/negative`,
      fault_flags_prefix: `battery/measurement/${i + 1}/fault_`,
      setpoint_active: `controller/${i + 1}/output/power_W`,
      setpoint_reactive: `controller/${i + 1}/output/power_VAr`,
      bess_onoff: `controller/${i + 1}/output/BESS_ONOFF`,
      dc_relays: `controller/${i + 1}/output/DCRelays`,
    },
    operating_profile: {
      watchdog_action: "zero_then_off",
      setpoint_timeout_s: 20,
      slew_fast_pu_s: 10,
      slew_slow_pu_s: 2,
      slew_threshold_kW: 10,
      interrogation_period_s: 60,
    },
  })),
  smartmeters: [
    {
      id: "sm-grid",
      name: "Grid coupling",
      role: "grid",
      protocol: "modbus-tcp",
      host: "192.168.115.110",
      port: 502,
      factor: -1,
      topics: {
        power_W: "smartmeter/measurement/1/power_W",
        power_VAr: "smartmeter/measurement/1/power_VAr",
        voltage_V_L1: "smartmeter/measurement/1/voltage_V_L1",
        voltage_V_L2: "smartmeter/measurement/1/voltage_V_L2",
        voltage_V_L3: "smartmeter/measurement/1/voltage_V_L3",
        current_A: "smartmeter/measurement/1/current_A",
        frequency_Hz: "smartmeter/measurement/1/frequency_Hz",
      },
    },
  ],
  controller: {
    id: "controller-1",
    grid_smartmeter_id: "sm-grid",
    modes: ["gridZeroControl", "PeakShaving", "ConstantPower", "OFF"],
    schedule_attribute: "bess_schedule",
    diagnostic_topic: "controller/diagnostics",
  },
  prices: {
    zone: "IT-NORD",
    currency: "EUR",
    day_ahead_attribute: "next_day_prices",
    spot_attribute: "intraday_price_eur_mwh",
    carbon_intensity_g_kWh: 245,
  },
  scadaPreset: "wide",
  // Synthetic demo site — the LV-bus claim is part of the fiction and
  // safe to assert here (real-site topologies omit this unless known).
  bus_label: "SITE BUS · 400 V · 50 Hz",
};
